/**
 * PlacesFilterControlsMixin
 *
 * Shared filter-state wiring for the places filtering UX (`PlacesControls` +
 * `FilterPanel`), consumed by BOTH the Places page (`PlacesPage.vue`) and the
 * place-selection Select tab (`PlaceSelectionSelectTab.vue`). Extracting this
 * wiring into a single mixin is what makes "define once, stays consistent"
 * (Req 9.1-9.4) enforceable rather than a copy that drifts.
 *
 * The mixin owns everything identical across the two surfaces: the active
 * filter state, source selection, and the filter/suggestion/cascade handlers.
 * It delegates the one thing that differs -- how results are fetched and
 * rendered -- to a host-provided `loadResults()` method (template-method
 * pattern), and it clears the host's selection through a host-provided
 * `resetSelection()` method.
 *
 * ---------------------------------------------------------------------------
 * HOST CONTRACT
 * ---------------------------------------------------------------------------
 * A component that mixes in `PlacesFilterControlsMixin` MUST also:
 *
 *   1. Mix in `Places_Service` (`src/services/dataExplorer/placesService.js`),
 *      which provides `this.filters` (the `PlacesFilters` set) and the
 *      service methods this mixin calls: `searchCriteria`,
 *      `getCustomCategories`, `getCustomPoilists`, `getTenantCountry`,
 *      `getPlacesFilteredPolygons`, and `getPlacesSearchPois`.
 *
 *   2. Mix in `GetMixin` (from pinnacle-lib), which provides the auth helpers
 *      (`tokenNeedsRefresh`, `refreshToken`, `getHeaders`) the service methods
 *      rely on, plus `this.$http`.
 *
 *   3. Expose a `pagination` object in its own `data()` with a `page` field,
 *      so the shared handlers can reset it to page 1 before reloading.
 *
 *   4. Implement `loadResults()` -- fetch + render results for the current
 *      source/filter/page state. `PlacesPage` builds table rows; the Select
 *      tab builds the checkbox list + GeoJSON and emits to the map.
 *
 *   5. Implement `resetSelection()` -- clear the host's current selection
 *      (Select tab: uncheck locations + clear `selectedIndexes`;
 *      PlacesPage: clear `selectedIds`).
 *
 * A host may override `searchDebounceMs` in its own `data()` to change the
 * search debounce interval (Places page: 350 ms, Select tab: 500 ms) without
 * changing any other observable behavior.
 */
import { applyAdminRegionLabels, resetAdminRegionLabels } from '@/services/dataExplorer/placesService';

export default {
  data() {
    return {
      // Free-text search term, capped at 256 characters (Req 1.2).
      searchTerm: '',
      // Timer handle for the debounced search reload.
      searchDebounce: null,
      // Debounce interval in ms; hosts override (350 page / 500 tab, Req 1.3).
      searchDebounceMs: 350,
      // Currently selected Source; defaults to 'Azira' (Req 3.2).
      sourceFilter: 'Azira',
      // Source dropdown options (Req 3.1).
      sourceOptions: ['Azira', 'Custom'],
      // Active_Filters: selected value (option id) per filter param;
      // '' means the filter is not set (Req 2.5, 4.3).
      activeFilters: {
        cat2: '',
        brand: '',
        country: '',
        state: '',
        city: '',
        placesType: '',
        tag: '',
      },
      // Human-readable labels for active-filter chips, keyed by param (Req 2.7).
      activeFilterLabels: {},
      // Typeahead suggestions per filter param -> option[] (Req 1.4, 4.6).
      filterSuggestions: {},
      // Per-filter loading flags for suggestion requests (Req 4.6).
      filterLoading: {},
      // Per-filter error flags for suggestion requests (Req 4.8, 5.5).
      filterErrors: {},
      // Level -> display name map for the active country's admin regions;
      // drives dynamic State/City labels and visibility (Req 2.1-2.6).
      levelNameMap: {},
      // True when the last admin-region lookup failed (Req 5.5).
      adminRegionError: false,
    };
  },
  computed: {
    /**
     * The filters available for the currently selected Source: exactly those
     * whose `sources` array includes `sourceFilter`. Reuses the `sources`
     * metadata already encoded on each `PlacesFilters` entry so both the
     * Places page and the Select tab expose the identical set for a given
     * Source (Req 4.1, 4.2, 9.3). Filter defs marked `hidden` (e.g. an
     * admin-region filter with no matching level for the active country) are
     * excluded so they never render (Req 2.6, 5.5).
     */
    availableFilters() {
      if (!this.filters) return [];
      return this.filters.filter(f => f.sources && f.sources.includes(this.sourceFilter) && !f.hidden);
    },
    /**
     * The number of active filters, where an active filter is one with a
     * non-empty selected value. Drives the Filters toggle badge (Req 2.5, 2.6).
     */
    activeFilterCount() {
      return Object.values(this.activeFilters).filter(v => !!v).length;
    },
    /**
     * The tenant Company_Id used for Custom-source requests and Custom
     * Category/Brand suggestion scoping, resolved from the persisting store's
     * user state (Req 3.4, 3.5, 4.7).
     */
    selectedCompanyId() {
      const userState = this.$persistingStore?.state?.user || {};
      return userState.selected_company_id || userState.defaultCompanyId || "";
    },
  },
  methods: {
    /**
     * Classify a filter as required solely by reading the declarative `required`
     * flag from its definition in `this.filters` (found by param). Never
     * compares the param to 'country' (R1.5). Any filter whose def is missing,
     * or whose `required` is not the boolean `true`, is treated as NOT required
     * (R1.2). Guards against a missing/empty `this.filters` so an unknown param
     * resolves to not-required.
     *
     * @param {string} param the filter param to classify
     * @returns {boolean} true iff the matching def has `required === true`
     */
    _isRequiredFilter(param) {
      const def = (this.filters || []).find(f => f && f.param === param);
      return !!(def && def.required === true);
    },
    /**
     * Handle a change to the free-text search term. The value is capped at 256
     * characters (Req 1.2), then a debounced reload is scheduled: after
     * `searchDebounceMs` (350 ms page / 500 ms tab, Req 1.3) the pagination is
     * reset to the first page and the host's `loadResults()` runs (Req 8.7).
     *
     * @param {string} value the raw search term from the input
     */
    onSearchChange(value) {
      this.searchTerm = (value || '').slice(0, 256);
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.pagination.page = 1;
        this.loadResults();
      }, this.searchDebounceMs);
    },
    /**
     * Record a selected filter value and reload from page one (Req 4.3). The
     * option id is stored in `activeFilters[filterKey]` (keyed by the filter's
     * `param`) and its human-readable name in `activeFilterLabels[filterKey]`
     * (reactive via `$set` so the chip label renders, Req 2.7).
     *
     * When the changed filter is Country, the value is recorded first and then
     * the shared `applyAdminRegionsForCountry` runs so the State/City labels and
     * visibility resolve for the new country. That call clears any stale
     * State/City selection (Req 4.4, 4.5), so it is awaited before `loadResults`
     * to ensure the reload reflects the cleared geo filters (Req 4.1, 6.2, 6.3).
     *
     * @param {{ filterKey: string, value: { id: *, name: string } }} payload
     */
    async onFilterChange({ filterKey, value }) {
      this.activeFilters[filterKey] = value.id;
      this.$set(this.activeFilterLabels, filterKey, value.name);
      if (filterKey === 'country') {
        await this.applyAdminRegionsForCountry(value.id);
      }
      this.pagination.page = 1;
      this.loadResults();
    },
    /**
     * Clear a single filter's value and label, applying the Cascading_Clear
     * invariant: clearing Country also clears State and City; clearing State
     * also clears City (Req 4.4, 5.1, 5.2, 9.4). Then reset to page one and
     * reload (Req 8.7).
     *
     * When Country is cleared, after the cascading clear the shared
     * `applyAdminRegionsForCountry('')` runs to restore the default State/City
     * labels and make both filters visible again (Req 4.2). It is awaited before
     * `loadResults` so the reload reflects the reset admin-region state.
     *
     * @param {string} filterKey the param of the filter being cleared
     */
    async onFilterClear(filterKey) {
      // Guard: a Required_Filter can never be cleared to empty. When the target
      // is required, make no state change, no pagination reset, and no reload
      // (R2.1, R2.2). Classification reads the declarative flag via
      // `_isRequiredFilter`, never by comparing the param to 'country' (R1.5).
      if (this._isRequiredFilter(filterKey)) {
        return;
      }
      this.activeFilters[filterKey] = '';
      this.$set(this.activeFilterLabels, filterKey, '');
      if (filterKey === 'country') {
        this.activeFilters.state = '';
        this.activeFilters.city = '';
        this.$set(this.activeFilterLabels, 'state', '');
        this.$set(this.activeFilterLabels, 'city', '');
        await this.applyAdminRegionsForCountry('');
      }
      if (filterKey === 'state') {
        this.activeFilters.city = '';
        this.$set(this.activeFilterLabels, 'city', '');
      }
      this.pagination.page = 1;
      this.loadResults();
    },
    /**
     * Clear every non-required active filter and its chip while preserving each
     * Required_Filter's value and label, then reset to page one and reload once
     * with the resulting filter state (R3.1, R3.2, R3.3, R3.5, R3.6).
     *
     * Required status is read from `this.filters` per param via
     * `_isRequiredFilter`, never by comparing the param to 'country' (R1.5),
     * so the retained Country value keeps POI search functional (R3.4, R8.4).
     *
     * If no Required_Filter holds a non-empty value, nothing is preserved: every
     * filter clears, page resets, and results reload exactly once without error
     * (R3.7).
     */
    onClearAllFilters() {
      const params = Object.keys(this.activeFilters);
      const nextActive = {};
      const nextLabels = {};
      params.forEach((param) => {
        if (this._isRequiredFilter(param)) {
          // Preserve required filters' value + label exactly (R3.1, R3.2, R3.4).
          nextActive[param] = this.activeFilters[param];
          if (this.activeFilterLabels[param] !== undefined) {
            nextLabels[param] = this.activeFilterLabels[param];
          }
        } else {
          // Clear every non-required filter to empty (R3.3).
          nextActive[param] = '';
        }
      });
      this.activeFilters = nextActive;
      this.activeFilterLabels = nextLabels;
      this.pagination.page = 1;
      this.loadResults();
    },
    /**
     * Handle a change of the selected Source. Clears the host's current
     * selection via the host-provided `resetSelection()` hook and resets
     * pagination to the first page (Req 3.6). When switching to 'Azira', the
     * Custom-only Place Type and Tag filter values and labels are cleared
     * (Req 4.5). Finally reloads results for the new source (Req 3.6).
     *
     * `PlacesPage` overrides this to also persist the source in the URL, then
     * delegates to this shared method.
     *
     * @param {string} value the newly selected Source ('Azira' | 'Custom')
     */
    onSourceChange(value) {
      this.sourceFilter = value;
      // Remember the selected Source as a durable user preference so it becomes
      // the default the next time the Places page or Place Selection modal opens.
      if (this.$persistingStore) {
        this.$persistingStore.commit('setLastSelectedSource', value);
      }
      this.resetSelection();
      this.pagination.page = 1;
      // Clear filters not applicable to the new source (Req 4.5).
      if (value === 'Azira') {
        this.activeFilters.placesType = '';
        this.activeFilters.tag = '';
        this.$set(this.activeFilterLabels, 'placesType', '');
        this.$set(this.activeFilterLabels, 'tag', '');
      }
      this.loadResults();
    },
    /**
     * The last Source the user selected, read from the durable user
     * preferences (localStorage-backed persisting store). Falls back to
     * 'Azira' when no preference is stored or the store is unavailable. Hosts
     * call this in `created()` to seed `sourceFilter` so the previously chosen
     * Source becomes the default the next time the view opens.
     *
     * @returns {string} 'Azira' | 'Custom'
     */
    getPreferredSource() {
      const prefs = this.$persistingStore && this.$persistingStore.state.user
        ? this.$persistingStore.state.user.preferences
        : null;
      return prefs && prefs.lastSelectedSource === 'Custom' ? 'Custom' : 'Azira';
    },
    /**
     * Retrieve typeahead suggestions for a filter and hand them to
     * `PlacesControls`. Marks the filter loading and clears its error flag
     * up front (Req 4.6).
     *
     * For the Custom source, Category (`cat2`) and Brand (`brand`) suggestions
     * come from the tenant-specific Places API v3 endpoints
     * (`getCustomCategories` / `getCustomPoilists`) scoped to the active
     * Country value and the `selectedCompanyId` (Req 4.7). If no Country value
     * is present, suggestions are emptied and the request is skipped.
     *
     * For every other filter, suggestions come from `searchCriteria`, with
     * cascading geo-scoping: State suggestions are scoped to the active
     * Country, and City suggestions are scoped to the active Country and State
     * (Req 5.3, 5.4). This never touches `/v2/places-suggest` for retrieval
     * (Req 9.5).
     *
     * On failure the filter's error flag is set and the current `activeFilters`
     * values are left unchanged (Req 4.8, 5.5, 9.6). The loading flag is always
     * cleared in `finally`.
     *
     * @param {string} filterKey the param of the filter requesting suggestions
     * @param {string} searchText the typed text to search for
     */
    async onFilterSuggest(filterKey, searchText) {
      this.$set(this.filterLoading, filterKey, true);
      this.$set(this.filterErrors, filterKey, false);
      try {
        // For the Custom source, Category and Brand/POI List options come from
        // the tenant-specific Places API v3 endpoints rather than the shared suggest API.
        const SUGGESTION_LIMIT = 20;

        if (this.sourceFilter === 'Custom' && (filterKey === 'cat2' || filterKey === 'brand')) {
          const country = this.activeFilters.country;
          if (!country) {
            this.$set(this.filterSuggestions, filterKey, []);
            return;
          }
          const options = { companyId: this.selectedCompanyId, searchText, limit: SUGGESTION_LIMIT };
          const results = filterKey === 'cat2'
            ? await this.getCustomCategories(country, options)
            : await this.getCustomPoilists(country, options);
          this.$set(this.filterSuggestions, filterKey, results);
          return;
        }

        // For the Custom source, Tag options come from the Places API v3
        // labels/search endpoint via the pinnacle-api proxy rather than the
        // shared suggest API. Scoped to the active Country value and the
        // selectedCompanyId; if no Country value is present, suggestions are
        // emptied and the request is skipped.
        if (this.sourceFilter === 'Custom' && filterKey === 'tag') {
          const country = this.activeFilters.country;
          if (!country) {
            this.$set(this.filterSuggestions, filterKey, []);
            return;
          }
          const results = await this.searchLabels({
            country,
            wildSearch: searchText,
            companyId: this.selectedCompanyId,
            limit: 50,
          });
          this.$set(this.filterSuggestions, filterKey, results);
          return;
        }

        const filter = this.filters.find(f => f.param === filterKey);
        const query = { name: searchText, page_size: SUGGESTION_LIMIT };
        if (filterKey === 'state' && this.activeFilters.country) {
          query.country = this.activeFilters.country;
        }
        if (filterKey === 'city') {
          if (this.activeFilters.country) query.country = this.activeFilters.country;
          if (this.activeFilters.state) query.state = this.activeFilters.state;
        }
        const results = await this.searchCriteria(filter, query);
        this.$set(this.filterSuggestions, filterKey, results);
      } catch (error) {
        console.error(`Failed to fetch suggestions for ${filterKey}`, error);
        this.$set(this.filterErrors, filterKey, true);
      } finally {
        this.$set(this.filterLoading, filterKey, false);
      }
    },
    /**
     * Preselect the tenant company's country as the default Country filter.
     * Only applied when no Country value is already set, and never overrides an
     * existing selection (Req 6.1, 6.2). Resolves the tenant country through
     * `getTenantCountry(selectedCompanyId)`; when it returns a truthy
     * `{ id, name }` and Country is still unset, sets the Country value and its
     * chip label (reactive via `$set`).
     *
     * When the tenant-country lookup fails (the `/v2/tenant-country?companyId=`
     * call errors) or returns a null/empty result, USA is used as the default
     * Country filter instead of leaving it unset, resolved via
     * `resolveDefaultUsaCountry()`.
     *
     * After the country is defaulted, the shared `applyAdminRegionsForCountry`
     * runs for the resolved Country value so the State/City labels and
     * visibility resolve during initialization before results are loaded
     * (Req 4.3, 6.2, 6.3). Both host `created()` hooks already await this method
     * before calling `loadResults()`, so no host change is required.
     */
    async applyDefaultCountryFilter() {
      if (this.activeFilters.country) return;
      let tenantCountry = null;
      try {
        tenantCountry = await this.getTenantCountry(this.selectedCompanyId);
      } catch (error) {
        // Tenant-country lookup failed (e.g. the /v2/tenant-country?companyId=
        // call errored or the user has no NPAAS tenant). Fall through to the
        // USA default below rather than leaving Country unset.
        console.error('Failed to resolve tenant country; defaulting to USA', error);
        tenantCountry = null;
      }

      // Guard again in case the awaited call let another selection land.
      if (this.activeFilters.country) return;

      // Use the tenant country when the lookup returned a usable option;
      // otherwise fall back to USA as the default Country filter.
      const resolvedCountry = (tenantCountry && tenantCountry.id)
        ? tenantCountry
        : await this.resolveDefaultUsaCountry();

      if (resolvedCountry && resolvedCountry.id && !this.activeFilters.country) {
        this.activeFilters.country = resolvedCountry.id;
        this.$set(this.activeFilterLabels, 'country', resolvedCountry.name || resolvedCountry.id);
        await this.applyAdminRegionsForCountry(this.activeFilters.country);
      }
    },
    /**
     * Resolve the USA Country_Reference option used as the default when the
     * tenant-country lookup fails or returns nothing. Prefers the canonical
     * `{ id, name }` from `getCountryByAlpha3('USA')` (same resolver the
     * geocoder uses) so the chip label and admin-region cascade match a manual
     * selection. If that resolver itself fails or returns no match, falls back
     * to a literal `{ id: 'USA', name: 'United States' }` so a USA default is
     * always applied.
     *
     * @returns {Promise<{id: string, name: string}>}
     */
    async resolveDefaultUsaCountry() {
      try {
        const option = await this.getCountryByAlpha3('USA');
        if (option && option.id) return option;
      } catch (error) {
        // Swallow: fall through to the literal USA default below.
      }
      return { id: 'USA', name: 'United States' };
    },
    /**
     * Apply a geocoded ISO alpha-3 country code to the Country filter, reusing
     * the same country-change path as a manual selection so the canonical chip
     * label, always-overwrite behavior, and the standard Country_Cascade (clear
     * State/City, reload page 1) all come for free.
     *
     * Behavior:
     *  - Normalizes the input (trim + uppercase) and returns early on empty
     *    input, leaving all filter state untouched.
     *  - Resolves the code to a `{ id, name }` Country_Reference option via
     *    `getCountryByAlpha3` (mixed in from Places_Service). Any thrown error
     *    is swallowed so the feature fails open with no user-facing error and
     *    no side effect (Req 5.3).
     *  - On a `null`/no-`id` result, returns without touching
     *    `activeFilters`/`activeFilterLabels` or any State/City selection
     *    (Req 4.3, 5.2).
     *  - On a match, calls `onFilterChange({ filterKey: 'country', value: option })`
     *    unconditionally -- with no equality guard against the current value --
     *    so the cascade always runs (Req 3.1, 3.2, 3.3, 3.4, 4.2).
     *
     * @param {string} alpha3 the derived ISO alpha-3 country code
     * @returns {Promise<void>}
     */
    async applyGeocodedCountry(alpha3) {
      const code = (alpha3 || '').trim().toUpperCase();
      if (!code) return;
      let option = null;
      try {
        option = await this.getCountryByAlpha3(code);
      } catch (error) {
        // Swallow: graceful degradation, no user-facing error (Req 5.3).
        return;
      }
      if (!option || !option.id) return;
      await this.onFilterChange({ filterKey: 'country', value: option });
    },
    /**
     * Resolve and apply the country-driven dynamic labels/visibility for the
     * State and City admin-region filters. This is the single shared entry
     * point invoked when the Country value is selected, defaulted, changed, or
     * cleared, so both consuming surfaces stay consistent (Req 6.1).
     *
     * Behavior:
     *  - Empty / whitespace country: no request is issued; the Level_Name_Map
     *    is reset to `{}`, both filters are restored to their default labels
     *    and made visible, and any State/City selection + label is cleared
     *    (Req 1.2, 4.2, 4.4). `adminRegionError` is cleared.
     *  - Non-empty country: clears any stale State/City selection + label up
     *    front (Req 4.4, 4.5), then requests the admin regions via the shared
     *    `getAdminRegionsForCountry` service method (mixed in from
     *    Places_Service, called on `this`).
     *      - Failure (`ok === false`): restore default labels + visibility
     *        (Req 5.1), set `adminRegionError` and surface a user-visible toast
     *        where available (Req 5.5), and leave `activeFilters.country` (as
     *        well as state/city) untouched (Req 1.9, 5.2). Never hides a filter
     *        on failure (Req 5.6).
     *      - Success: record the Level_Name_Map, compute the next filter set
     *        via the pure `applyAdminRegionLabels`, clear the selection + label
     *        of any filter the mapping hides (Req 2.8), validate the `param`
     *        invariant for the relabeled filters (Req 3.6), and assign
     *        `this.filters` exactly once so `availableFilters` recomputes
     *        atomically with no stale intermediate state (Req 2.7, 4.5).
     *
     * @param {string} country the Country_Value (option id)
     * @returns {Promise<void>}
     */
    async applyAdminRegionsForCountry(country) {
      const trimmed = (country || '').trim();

      if (trimmed === '') {
        this.levelNameMap = {};
        this.filters = resetAdminRegionLabels(this.filters);
        this.activeFilters.state = '';
        this.activeFilters.city = '';
        this.$set(this.activeFilterLabels, 'state', '');
        this.$set(this.activeFilterLabels, 'city', '');
        this.adminRegionError = false;
        return;
      }

      // Clear any stale State/City selection from the previous country before
      // the lookup so no prior selection survives a country change (Req 4.4, 4.5).
      this.activeFilters.state = '';
      this.activeFilters.city = '';
      this.$set(this.activeFilterLabels, 'state', '');
      this.$set(this.activeFilterLabels, 'city', '');
      this.adminRegionError = false;

      const { ok, levelNameMap } = await this.getAdminRegionsForCountry(trimmed);

      if (!ok) {
        // Failure branch: keep both filters visible with default labels; never
        // hide on failure and never touch country/state/city selections.
        this.filters = resetAdminRegionLabels(this.filters);
        this.adminRegionError = true;
        if (this.$bvToast && typeof this.$bvToast.toast === 'function') {
          this.$bvToast.toast('Administrative-region data could not be loaded.', {
            title: 'Filters',
            variant: 'warning',
            noAutoHide: false,
          });
        }
        return;
      }

      this.levelNameMap = levelNameMap;
      const next = applyAdminRegionLabels(this.filters, levelNameMap);

      // Validate the param invariant: the relabeled State/City defs must keep
      // their `state`/`city` param binding. On violation discard the mapping
      // and surface the error rather than binding a filter to a wrong param
      // (Req 3.6).
      const stateDef = next.find(f => f && f.id === 'state-filter');
      const cityDef = next.find(f => f && f.id === 'city-filter');
      if ((stateDef && stateDef.param !== 'state') || (cityDef && cityDef.param !== 'city')) {
        this.levelNameMap = {};
        this.adminRegionError = true;
        return;
      }

      // Clear the selection + label of any filter the mapping hides so a hidden
      // filter contributes no active geo filter (Req 2.8).
      if (stateDef && stateDef.hidden) {
        this.activeFilters.state = '';
        this.$set(this.activeFilterLabels, 'state', '');
      }
      if (cityDef && cityDef.hidden) {
        this.activeFilters.city = '';
        this.$set(this.activeFilterLabels, 'city', '');
      }

      // Single atomic assignment so availableFilters recomputes in one tick.
      this.filters = next;
    },
  },
};
